import { formatInTimeZone } from 'date-fns-tz';
import { resolveDayOf } from 'src/pages/Map/displayDays';

import {
  boxRect, buildDayBoxes, ColumnWindow, DaySpan, FULL_COLUMN, windowOf,
} from './buildDayBoxes';
import { businessDayOf, DropTarget } from './buildDropTargets';

import type { Lane, LongHaulTrip } from './types';

/** 'YYYY-MM-DD' → 'M/D' */
const monthDayLabel = (day: string): string => {
  const [, month, date] = day.split('-');
  return `${Number(month)}/${Number(date)}`;
};

export type TripBarLayout = {
  trip: LongHaulTrip;
  left: number;
  width: number;
  /**
   * 段組みしたときの段番号（0 始まり）。段の高さは `stackCount` から描画側が決める。
   * ここで px にしないのは、レーンの高さを知っているのが描画側だけのため。
   */
  stackSlot: number;
  /** 期間より前に積みが始まる運行に出すバッジ。出さないときは null */
  startsBeforeLabel: string | null;
};

export type WorkBandLayout = {
  target: DropTarget;
  left: number;
  width: number;
  /** 帯の中で実際に働く時間帯。時刻を読めないときは null */
  worked: { left: number; width: number } | null;
};

export type LaneBoxLayout = {
  bars: TripBarLayout[];
  /**
   * レーンに要る段の数。1 なら段組みしない（今までと同じ高さ）。
   *
   * レーン全体で 1 つにするのは、段の高さを行の中で揃えるため。クラスタごとに
   * 変えると、同じ行なのに隣り合うバーの高さが食い違う。
   */
  stackCount: number;
  bands: WorkBandLayout[];
};

/** 勤務 1 件ぶんの割り当て単位。勤務帯とその勤務に載る運行をまとめて 1 つの箱に入れる */
type WorkUnit = DaySpan & {
  key: string;
  target: DropTarget | null;
};

/** 日カラム範囲を解けたバー 1 本 */
type TripEntry = DaySpan & { trip: LongHaulTrip };

/**
 * 運行の日カラム範囲。クランプは `resolveDayOf` が期間外を端へ寄せるので済む。
 */
const tripDaySpan = (trip: LongHaulTrip, days: readonly string[]): DaySpan | null => {
  const startDay = resolveDayOf(trip.span.displayStartAt, days);
  const endDay = resolveDayOf(trip.span.displayEndAt, days);
  if (!startDay || !endDay) return null;

  const startDayIndex = days.indexOf(startDay);
  const endDayIndex = days.indexOf(endDay);
  if (startDayIndex < 0 || endDayIndex < 0) return null;
  return { startDayIndex, endDayIndex };
};

/**
 * バッジの判定だけは寄せる前の業務日で見る
 * （寄せたあとの日は必ず期間内なので、比べても常に偽になる）。
 */
const startsBeforeLabelOf = (trip: LongHaulTrip, days: readonly string[]): string | null => {
  const startBusinessDay = businessDayOf(trip.span.displayStartAt);
  return startBusinessDay != null && startBusinessDay < days[0]
    ? `◀ ${monthDayLabel(startBusinessDay)}積`
    : null;
};

const MINUTES_PER_DAY = 24 * 60;

/**
 * 業務日の中での位置（0〜1）。`dayKey` の日に入っていなければ端へ寄せる。
 *
 * Why not `new Date(iso).getHours()`: ブラウザ TZ で時刻が変わる。業務日は JST 固定なので
 * 時刻の取り出しも JST で揃える（`businessDayOf` と同じ規則）。
 */
const dayFraction = (iso: string, dayKey: string, fallback: 0 | 1): number => {
  const businessDay = businessDayOf(iso);
  // 期間の外は `resolveDayOf` が端の列へ寄せている。開始は前へ、終了は後ろへしか
  // はみ出さない（逆向きは `buildDropTargets` が帯ごと落とす）ので、端へ寄せた列では
  // 開始 = 列の左端 / 終了 = 列の右端 になる
  if (businessDay == null || businessDay !== dayKey) return fallback;

  const [hour, minute] = formatInTimeZone(new Date(iso), 'Asia/Tokyo', 'HH:mm').split(':');
  return (Number(hour) * 60 + Number(minute)) / MINUTES_PER_DAY;
};

/**
 * 帯の中で実際に働く時間帯。
 *
 * 帯の幅は日単位なので、午前休みで勤務が縮んでも帯は動かない。休みを入れた結果を
 * 盤面から読めるように、時刻に比例した位置を別に描く（設計書「日を共有するときは列を割る」）。
 * 箱が割れているときはその箱の幅の中で比例させる。
 */
const workedRect = (
  target: DropTarget,
  win: ColumnWindow,
  days: readonly string[],
  dayColWidth: number,
): { left: number; width: number } | null => {
  const startDay = days[target.startDayIndex];
  const endDay = days[target.endDayIndex];
  if (startDay == null || endDay == null) return null;

  const left = target.startDayIndex * dayColWidth + win.offset
    + dayFraction(target.startAt, startDay, 0) * win.width;
  const right = target.endDayIndex * dayColWidth + win.offset
    + dayFraction(target.endAt, endDay, 1) * win.width;
  // 時刻が壊れていても消さない（勤務があること自体は伝える）
  return { left, width: Math.max(1, right - left) };
};

/**
 * 勤務ごとの割り当て単位を組む。
 *
 * 単位の日範囲は「勤務帯の日」と「その勤務に載る運行の日」の和。勤務の枠から
 * はみ出した運行（勤務を縮めたあとに残った operation）があっても取りこぼさない。
 * 勤務に紐づかない運行（shiftTruckId が無い）は、それ自体を 1 単位として扱う。
 */
const buildWorkUnits = (
  lane: Lane,
  days: readonly string[],
  targets: readonly DropTarget[],
): WorkUnit[] => {
  const units = new Map<string, WorkUnit>();

  const unitFor = (key: string, span: DaySpan, target: DropTarget | null): WorkUnit => {
    const found = units.get(key);
    if (!found) {
      const created: WorkUnit = { key, target, ...span };
      units.set(key, created);
      return created;
    }
    found.startDayIndex = Math.min(found.startDayIndex, span.startDayIndex);
    found.endDayIndex = Math.max(found.endDayIndex, span.endDayIndex);
    if (target) found.target = target;
    return found;
  };

  for (const target of targets) {
    unitFor(`work:${target.shiftTruckId}`, target, target);
  }

  for (const trip of lane.trips) {
    const span = tripDaySpan(trip, days);
    if (!span) continue;
    // 勤務に紐づかない運行は barId で単位を分ける（他の運行と同じ箱に入れない）
    const key = trip.shiftTruckId == null ? `trip:${trip.barId}` : `work:${trip.shiftTruckId}`;
    unitFor(key, span, null);
  }

  return [...units.values()];
};

/**
 * レーン 1 本ぶんの矩形を出す。
 *
 * 勤務帯は日カラムを共有するものどうしで列を割る（帯が重なるとドロップ先が不定になる）。
 * 運行バーは列を割らず日カラムを丸ごと使い、日が重なるバーを上下の段へ分ける
 * （サンプル MultiDayBoard.tsx:695-706 と同じ）。
 *
 * Why not バーも列を割る（旧実装）: `boxRect` が返すのは開始列の窓の左端から終了列の
 * 窓の右端までの 1 つの矩形なので、複数日にまたがるバーは途中の列を丸ごと通る。
 * 152px の列を 4 つに割っても 2 日ぶんのバーは 190px になり、38px 先の隣の取り分へ
 * そのまま重なっていた（dev 実測 2026-09-29）。積地ごとにバーを分けたことで、
 * 同じ日に複数日ぶんのバーが並ぶ形が普通になったため、縦に分ける。
 */
export function buildLaneBoxLayout(
  lane: Lane,
  days: readonly string[],
  dayColWidth: number,
  targets: readonly DropTarget[],
): LaneBoxLayout {
  const units = buildWorkUnits(lane, days, targets);
  const unitBoxes = buildDayBoxes(units);
  const fullColumn = FULL_COLUMN(dayColWidth);

  const bands: WorkBandLayout[] = [];
  for (const unit of units) {
    if (!unit.target) continue;
    const unitBox = unitBoxes.get(unit) ?? { slot: 0, divisions: 1 };
    const unitWindow: ColumnWindow = windowOf(fullColumn, unitBox);
    bands.push({
      target: unit.target,
      ...boxRect(unit.target, unitWindow, dayColWidth),
      worked: workedRect(unit.target, unitWindow, days, dayColWidth),
    });
  }

  const entries: TripEntry[] = [];
  for (const trip of lane.trips) {
    const span = tripDaySpan(trip, days);
    if (span) entries.push({ ...span, trip });
  }
  // 段割りは `buildDayBoxes` の区間彩色をそのまま使う。段の数だけレーン全体へ揃える
  const stackBoxes = buildDayBoxes(entries);
  const stackCount = entries.reduce(
    (max, entry) => Math.max(max, stackBoxes.get(entry)?.divisions ?? 1),
    1,
  );
  // 描く順は入力どおり（レーンの trips の並び）。段は stackSlot が持つので並べ替えない
  const bars: TripBarLayout[] = entries.map((entry) => ({
    trip: entry.trip,
    ...boxRect(entry, fullColumn, dayColWidth),
    stackSlot: stackBoxes.get(entry)?.slot ?? 0,
    startsBeforeLabel: startsBeforeLabelOf(entry.trip, days),
  }));

  // 勤務帯だけは描く順をドロップ先の並びへ戻す（単位は Map から出るので順が崩れる）
  const bandOrder = new Map(targets.map((target, index) => [target.droppableId, index]));
  bands.sort(
    (a, b) => (bandOrder.get(a.target.droppableId) ?? 0) - (bandOrder.get(b.target.droppableId) ?? 0),
  );

  return { bars, stackCount, bands };
}
